import type { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useMemo } from "react";
import type { FieldPath, FieldValues } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";

import type { SimpleProjectDto, SystemGroupDto } from "@/api/types";
import { AudienceSelectorButton } from "@/components/AudienceSelector/AudienceSelectorButton";
import { Drawer } from "@/components/Drawer/Drawer";
import { Form } from "@/components/Form/Form";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormMultiSelect } from "@/components/Form/FormMultiSelect";
import { FormSelect } from "@/components/Form/FormSelect";
import { GroupIcon } from "@/components/GroupIcon/GroupIcon";
import { Building04Icon } from "@/components/Icons/Building04Icon";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import type { PillListItem } from "@/components/Pill/PillList";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { sortAlphabetically } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

type MassAudienceSelectorFormValues = {
  selectedGroup: SystemGroupDto;
  selectedProjects: SimpleProjectDto[];
};

type MassAudienceSelectorProps<TFormValues extends FieldValues> = {
  selectedGroup: SystemGroupDto | undefined;
  selectedProjects: SimpleProjectDto[];
  isError: boolean;
  isDisabled?: boolean;
  audienceGroupsQuery: ReturnType<typeof useQuery<SystemGroupDto[]>>;
  audienceProjectsQuery: ReturnType<typeof useQuery<SimpleProjectDto[]>>;
  subtitle: string;
  description: string | ((data: { projects: number }) => string);
  formErrorWrapperName: FieldPath<TFormValues>;
  onConfirmSelection: ({ group, projects }: { group: SystemGroupDto; projects: SimpleProjectDto[] }) => void;
  onInternalGroupChange?: (group: SystemGroupDto | undefined) => void;
};

export function MassAudienceSelector<TFormValues extends FieldValues>({
  selectedGroup,
  selectedProjects,
  isError,
  isDisabled = false,
  audienceGroupsQuery,
  audienceProjectsQuery,
  subtitle,
  description,
  formErrorWrapperName,
  onConfirmSelection,
  onInternalGroupChange,
}: MassAudienceSelectorProps<TFormValues>): React.ReactNode {
  const [isDrawerOpened, drawerHandler] = useBool(false);

  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const form = useForm<MassAudienceSelectorFormValues>({
    defaultValues: {
      selectedGroup: selectedGroup,
      selectedProjects: selectedProjects,
    },
    reValidateMode: "onChange",
    mode: "onChange",
  });
  const currGroup = useWatch({
    name: "selectedGroup",
    control: form.control,
  });
  const currProjects = useWatch({
    name: "selectedProjects",
    control: form.control,
  });

  const { data: audienceGroups = [] } = audienceGroupsQuery;
  const {
    data: audienceProjects = [],
    isFetching: isFetchingAudienceProjects,
    isPending: isPendingAudienceProjects,
  } = audienceProjectsQuery;

  const isProjectsLoading = isFetchingAudienceProjects || isPendingAudienceProjects;

  useEffect(() => {
    if (selectedGroup) {
      form.setValue("selectedGroup", selectedGroup);
    }

    form.setValue("selectedProjects", selectedProjects);
  }, [form, selectedGroup, selectedProjects]);

  useEffect(() => {
    // On group change make sure the projects are filtered to only include those available in the new group
    const currentProjects = form.getValues("selectedProjects");
    if (currentProjects.length > 0 && audienceProjects.length > 0) {
      const availableProjectIds = new Set(audienceProjects.map((p) => p.id));
      const filteredProjects = currentProjects.filter((project) => availableProjectIds.has(project.id));

      if (filteredProjects.length !== currentProjects.length) {
        form.setValue("selectedProjects", filteredProjects);
      }
    }
  }, [audienceProjects, form]);

  const handleSubmit = async () => {
    const isFormValid = await form.trigger();

    if (isFormValid) {
      const formValues = form.getValues();

      onConfirmSelection({
        group: formValues.selectedGroup,
        projects: formValues.selectedProjects,
      });

      drawerHandler.setFalse();
    }
  };

  const selectedAudienceItems = useMemo(() => {
    const items: PillListItem[] = [];

    if (!selectedGroup && selectedProjects.length === 0) {
      items.push({
        label: "[...]",
      });
    } else {
      if (selectedGroup) {
        items.push({
          icon: <GroupIcon size={16} icon={selectedGroup.icon} />,
          prefix: t("common.entity.group", {
            count: 1,
          }),
          label: selectedGroup.translations?.find((l) => l.languageId === sessionUser.language.id)?.name || "",
        });
      }

      const firstProject = selectedProjects[0];
      if (firstProject) {
        items.push({
          icon: <Building04Icon />,
          prefix: t("common.entity.project", {
            count: selectedProjects.length,
          }),
          label:
            selectedProjects.length > 1
              ? `${firstProject.name}, ${t("common.label.x-more", {
                  count: selectedProjects.length - 1,
                })}`
              : `${firstProject.name}`,
        });
      }
    }

    return items;
  }, [selectedGroup, selectedProjects, sessionUser.language.id, t]);

  return (
    <>
      <FormErrorWrapper<TFormValues> name={formErrorWrapperName}>
        <AudienceSelectorButton
          items={selectedAudienceItems}
          onClick={drawerHandler.setTrue}
          isError={isError}
          isDisabled={isDisabled}
          data-testid="audience-selector-btn"
        />
      </FormErrorWrapper>
      <Drawer.Root
        data-testid="audience-selector-drawer"
        title={t("page.admin-create-post.audience-selector.drawer.title")}
        isOpened={isDrawerOpened}
        onOpenChange={(state) => {
          if (!state) {
            form.reset();
          }

          drawerHandler.set(state);
        }}
      >
        <Drawer.Body>
          <Form.Root formMethods={form} onSubmit={() => {}}>
            <Stack gap="xl">
              <Stack gap="md">
                <h4 className="text-heading-4">{subtitle}</h4>
                <p className="text-body italic">
                  <MultiLineContent>
                    {typeof description === "function"
                      ? description({ projects: audienceProjects.length })
                      : description}
                  </MultiLineContent>
                </p>
              </Stack>
              <FormField label={t("page.create-mass.message.form.group")}>
                <FormSelect<MassAudienceSelectorFormValues, MassAudienceSelectorFormValues["selectedGroup"]>
                  name="selectedGroup"
                  emptyItem={t("page.create-mass-message.form.group.placeholder")}
                  items={[
                    ...audienceGroups.filter((x) => x.type === "interest"),
                    ...audienceGroups.filter((x) => x.type === "helpCategory"),
                  ]}
                  keySelector={(x) => x.id}
                  renderOption={(x) => (
                    <Inline gap="sm" align="center">
                      <GroupIcon size={16} icon={x.icon} />
                      <span>{x.translations?.find((l) => l.languageId === sessionUser.language.id)?.name}</span>
                    </Inline>
                  )}
                  groupSelector={(x) => {
                    switch (x.type) {
                      case "interest":
                        return t("page.create-mass-message.form.group.type.interest");
                      case "helpCategory":
                        return t("page.create-mass-message.form.group.type.help-category");
                      default:
                        return "";
                    }
                  }}
                  onChange={(value) => {
                    if (value?.id !== currGroup?.id) {
                      onInternalGroupChange?.(value);
                    }
                  }}
                  disabled={isProjectsLoading}
                />
              </FormField>
              <Stack gap="md">
                <FormField label={t("page.create-mass-message.form.projects")} required>
                  <FormMultiSelect<MassAudienceSelectorFormValues, "selectedProjects">
                    name="selectedProjects"
                    placeholder={t("page.create-mass-message.form.projects.placeholder")}
                    items={audienceProjects}
                    searchableFieldSelector={(project) => [project.name, project.owner, project.maintenance]}
                    renderOption={(project) => project.name}
                    keySelector={(project) => project.id}
                    sortSelectedItems={sortAlphabetically}
                    rules={{
                      required: t("components.form.error.required-generic"),
                    }}
                    disabled={isProjectsLoading || audienceProjects.length === 0}
                  />
                </FormField>
                <Stack gap="sm">
                  <Button
                    fullWidth
                    variant="soft"
                    onClick={() =>
                      form.setValue("selectedProjects", audienceProjects, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                    data-testid="add-all-projects-btn"
                    disabled={audienceProjects.length === currProjects.length}
                    isLoading={isProjectsLoading}
                  >
                    {t("page.create-mass-message.form.projects.add-all", {
                      count: audienceProjects.length,
                    })}
                  </Button>
                  <Button
                    fullWidth
                    variant="subtle"
                    tone="error"
                    onClick={() => form.setValue("selectedProjects", [])}
                    disabled={currProjects.length === 0}
                  >
                    {t("page.create-mass-message.form.projects.clear-all")}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Form.Root>
        </Drawer.Body>

        <Drawer.Actions>
          <Drawer.Close>
            <Button variant="subtle" tone="primary">
              {t("common.action.cancel")}
            </Button>
          </Drawer.Close>
          <Button data-testid="confirm-audience-btn" variant="filled" onClick={handleSubmit}>
            {t("common.action.select")}
          </Button>
        </Drawer.Actions>
      </Drawer.Root>
    </>
  );
}
